import React, { useEffect, useState } from 'react';
import { setIsTermsAccepted } from '../../redux/slices/termsAndSubSlice';
import { TextFieldControl } from '../form-fields/TextField';
import { PaymentProps as PaymentFieldProps } from './Payment';
import { AddressForm } from '../address/AddressForm';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import {
  getBorderProps,
  getcurrentDeviceType,
  getFontSize,
  getMarginStyles,
  getNewMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import LockIcon from '../../../assets/svg/lock';
import QuestionIcon from '../../../assets/svg/question';
import { EventPublisher } from '../../utils/publishEvent';
import { v4 as uuidv4 } from 'uuid';
import { Shop } from '../../interface/storeInterface';
import { Cart } from '../../interface/checkoutInterface';
import { FormData } from '../../../page-builder/interface/checkoutInterface';
import CollectForm from '../payment-vgs/CollectForm';
import { getIsViewIsEdit } from '../../utils/constants';
import CreditCardLogos from '../../../assets/svg/credit-logos';
import Toggle from '../../../components/common/toggleSwitchCustom';

interface PaymentProps {
  handleReturnToPayment: () => void;
}

type CustomInputEvent = React.ChangeEvent<HTMLInputElement> & {
  nativeEvent: {
    inputType: string;
  };
};

interface PaymentControllerProps {
  fields: PaymentFieldProps;
  fromRadio: boolean;
  storeData: Shop;
  cartData: Cart | null;
}

export const PaymentFields: React.FC<
  PaymentProps &
    PaymentControllerProps &
    ReduxSelectorProps &
    ReduxDispatchProps & {
      editMode: boolean | undefined;
    }
> = ({
  fields,
  fromRadio,
  useAppSelector,
  useAppDispatch,
  cartData,
  storeData,
  editMode,
}) => {
  const { watch, setValue } = useFormContext<FormData>();
  const { useNewExpCvvLayout } = fields;

  const {
    margin,
    isShowSubText,
    isShowBillingAddress,
    isShowNameOnCard,
    subText,
    paymentPlaceholder,
    heightField,
    option,
    termsAndSub,
    padding,
    border,
    headerLogoCustomization,
    svgCustomization,
    billingAddressLabel,
  } = fields;

  if (
    (getIsViewIsEdit() && option?.CountryCode?.defaultCountry?.length) ||
    (watch('BillingAddress.CountryCode').length == 0 &&
      option?.CountryCode?.defaultCountry?.length &&
      option?.CountryCode?.defaultCountry?.length > 0 &&
      !watch('billing_same_as_shipping') &&
      isShowBillingAddress)
  ) {
    setValue('BillingAddress.CountryCode', option?.CountryCode?.defaultCountry);
  }

  const windowWidth = useAppSelector(state => state.store.currentWidth);
  const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
  const marginStyles = getMarginStyles(margin, currentDeviceType);
  const [isOpen, setIsOpen] = useState(termsAndSub?.defaultCollapse);
  const dispatch = useAppDispatch();
  const isTermsAccepted = useAppSelector(
    state => state.termsAndSub.isTermsAccepted,
  );
  const Gap = getFontSize(headerLogoCustomization?.gap);
  const publish = EventPublisher.getInstance().publish;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };
  const state = useAppSelector(state => state);
  useEffect(() => {
    setIsOpen(termsAndSub.defaultCollapse);
  }, [termsAndSub.defaultCollapse]);
  const svgWidth = isNaN(
    +svgCustomization?.imageSize.maxWidth[currentDeviceType],
  )
    ? svgCustomization?.imageSize.maxWidth[currentDeviceType]
    : `${+svgCustomization?.imageSize.maxWidth[currentDeviceType]}`;
  const svgHeight = isNaN(
    +svgCustomization?.imageSize.height[currentDeviceType],
  )
    ? svgCustomization?.imageSize.height[currentDeviceType]
    : `${+svgCustomization?.imageSize.height[currentDeviceType]}`;
  const htmlWidth = headerLogoCustomization?.imageFile?.width || undefined;
  const htmlHeight = headerLogoCustomization?.imageFile?.height || undefined;
  const imgWidth = isNaN(
    +headerLogoCustomization?.imageSize?.maxWidth?.[currentDeviceType],
  )
    ? headerLogoCustomization?.imageSize?.maxWidth?.[currentDeviceType]
    : `${+headerLogoCustomization?.imageSize?.maxWidth?.[currentDeviceType]}px`;
  const imgHeight = isNaN(
    +headerLogoCustomization?.imageSize?.height?.[currentDeviceType],
  )
    ? headerLogoCustomization?.imageSize?.height?.[currentDeviceType]
    : `${+headerLogoCustomization?.imageSize?.height?.[currentDeviceType]}px`;
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [check, setCheck] = useState<boolean>(true);
  const [isLogoLoaded, setIsLogoLoaded] = useState<boolean>(false);

  return (
    <section
      aria-labelledby="payment-heading"
      className={`${border?.borderStyle?.[currentDeviceType]} text-editor-content`}
      style={{
        backgroundColor: fields.background?.isInherit
          ? 'inherit'
          : fields.background?.color,
        ...marginStyles,
        ...getPaddingStyles(padding, currentDeviceType),
        ...getBorderProps(border, currentDeviceType),
      }}>
      {!fromRadio &&
        subText?.showComponent?.[currentDeviceType] &&
        isShowSubText && (
          <p
            className={`${subText?.fontSize?.[currentDeviceType]} ${subText?.weight?.[currentDeviceType]} mb-2`}
            style={{
              color: subText?.textColor?.[currentDeviceType],
            }}>
            {subText.text}
          </p>
        )}
      <div className="rounded-md">
        {!fromRadio && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="border rounded-t-md"
            style={{
              background: isHovered
                ? headerLogoCustomization?.color
                : fields?.header?.background?.isInherit
                  ? 'inherit'
                  : fields?.header?.background?.color,
              borderColor: fields?.header?.borderColor,
              ...getNewMarginStyles(
                headerLogoCustomization?.margin,
                currentDeviceType,
              ),
              ...getPaddingStyles(
                headerLogoCustomization?.padding,
                currentDeviceType,
              ),
            }}>
            <div
              className={`flex items-center w-full ${'gap-' + svgCustomization?.gap}`}>
              <div
                className={`flex items-center space-x-2 ${headerLogoCustomization?.justifyContent?.justify?.[currentDeviceType]} w-full`}
                style={{
                  gap: Gap,
                }}>
                {headerLogoCustomization?.showHeaderLogo && (
                  <img
                    src={
                      process.env.REACT_APP_S3_BUCKET_URL +
                      headerLogoCustomization?.imageFile.imageUrl
                    }
                    alt={headerLogoCustomization?.imageFile?.fileName}
                    decoding="async"
                    width={htmlWidth}
                    height={htmlHeight}
                    onLoad={() => setIsLogoLoaded(true)}
                    style={{
                      ...(isLogoLoaded
                        ? currentDeviceType &&
                          headerLogoCustomization?.imageSize?.maxWidth &&
                          headerLogoCustomization?.imageSize?.height &&
                          headerLogoCustomization?.imageSize?.maxWidth[
                            currentDeviceType
                          ] !== undefined &&
                          headerLogoCustomization?.imageSize?.height[
                            currentDeviceType
                          ] !== undefined
                          ? {
                              maxWidth: isNaN(
                                +headerLogoCustomization?.imageSize.maxWidth[
                                  currentDeviceType
                                ],
                              )
                                ? headerLogoCustomization?.imageSize.maxWidth[
                                    currentDeviceType
                                  ]
                                : `${+headerLogoCustomization?.imageSize.maxWidth[currentDeviceType]}px`,
                              height: isNaN(
                                +headerLogoCustomization?.imageSize.height[
                                  currentDeviceType
                                ],
                              )
                                ? headerLogoCustomization?.imageSize.height[
                                    currentDeviceType
                                  ]
                                : `${+headerLogoCustomization?.imageSize.height[currentDeviceType]}px`,
                            }
                          : {}
                        : {
                            maxWidth: imgWidth,
                            height: imgHeight,
                          }),
                    }}
                  />
                )}
                <p className="text-sm text-black titleStyles">
                  {paymentPlaceholder?.Title?.placeholder}
                </p>
              </div>

              <CreditCardLogos
                className="h-8"
                showSVG={fields.showSVG}
                svgWidth={svgWidth}
                svgHeight={svgHeight}
              />
            </div>

            {paymentPlaceholder?.Title && (
              <style>
                {`
        .titleStyles {
          font-size:  ${getFontSize(paymentPlaceholder?.Title?.fontSize)};
          font-weight: ${paymentPlaceholder?.Title?.fontWeight};
          line-height: ${paymentPlaceholder?.Title?.lineHeight};
          text-align: ${paymentPlaceholder?.Title?.textAlign};
          font-family: ${paymentPlaceholder?.Title?.fontFamily};
          letter-spacing: ${paymentPlaceholder?.Title?.letterSpace}px;
          color: ${paymentPlaceholder?.Title?.color};
        }
      `}
              </style>
            )}
          </div>
        )}

        <div
          className={`p-5 bg-custom-gray ${fromRadio ? 'border-t bg-page_builder_theme bg-opacity-5 rounded-t-md' : ''} border-b border-l border-r rounded-b-md`}>
          {storeData?.IsCardEncryption && !editMode ? (
            <CollectForm
              useAppSelector={useAppSelector}
              useAppDispatch={useAppDispatch}
              fields={fields}
              currentDeviceType={currentDeviceType}
              editMode={editMode}
            />
          ) : (
            <div
              className={`${
                useNewExpCvvLayout
                  ? 'grid grid-cols-4 sm:grid-cols-4'
                  : 'grid grid-cols-3 sm:grid-cols-4'
              }`}
              style={{
                gridRowGap: isNaN(
                  Number(heightField?.rowGap?.[currentDeviceType]),
                )
                  ? heightField?.rowGap?.[currentDeviceType]
                  : Number(heightField?.rowGap?.[currentDeviceType]),
                gridColumnGap: isNaN(
                  Number(heightField?.colGap?.[currentDeviceType]),
                )
                  ? heightField?.colGap?.[currentDeviceType]
                  : Number(heightField?.colGap?.[currentDeviceType]),
              }}>
              <div className="col-span-4">
                <TextFieldControl
                  name={'CardInfo.Number'}
                  defaultValue=""
                  fieldHeight={heightField?.height?.[currentDeviceType]}
                  radiusBorder={fields?.paymentPlaceholder?.Number?.border}
                  outline={fields?.paymentPlaceholder?.Number?.outline}
                  maxLength={23} // max length of 19 digits + 4 spaces
                  header={paymentPlaceholder?.Number?.headers}
                  placeholder={paymentPlaceholder?.Number?.placeholder}
                  textColor={paymentPlaceholder?.Number?.textColor}
                  backgroundColor={
                    paymentPlaceholder?.Number?.textBackgroundColor
                  }
                  placeHolderFontSize={paymentPlaceholder?.Number?.fontSize}
                  placeHolderweight={paymentPlaceholder?.Number?.fontWeight}
                  placeHolderLineHeight={paymentPlaceholder?.Number?.lineHeight}
                  PlaceHolderTextAlign={paymentPlaceholder?.Number?.textAlign}
                  placeHolderFontFamily={paymentPlaceholder?.Number?.fontFamily}
                  placeHolderLetterSpace={
                    paymentPlaceholder?.Number?.letterSpace
                  }
                  placeHolderColor={paymentPlaceholder?.Number?.color}
                  currentDeviceType={currentDeviceType}
                  InputIcon={LockIcon}
                  valueFormatter={cardNumber => {
                    // This function formats the card number to have spaces every 4 digits
                    const rawText = cardNumber.split('');
                    const result: string[] = [];
                    rawText.forEach((t, i) => {
                      if (i % 4 === 0 && i !== 0) result.push(' ');
                      result.push(t);
                    });
                    return result.join('');
                  }}
                  onChangeHandler={e => {
                    return e.target.value.replace(/[^0-9]/g, '');
                  }}
                  rules={{
                    required: 'Card number is required',
                    validate: value => {
                      const cardNumberWithoutSpace = value.replace(/\s/g, '');
                      if (
                        cardNumberWithoutSpace.length < 13 ||
                        cardNumberWithoutSpace.length > 19
                      ) {
                        return 'Card number must be 13-19 digits and a recognizable card format';
                      }
                    },
                  }}
                />
              </div>
              <div
                className={`${
                  useNewExpCvvLayout
                    ? '2xl:col-span-2 xl:col-span-2 lg:col-span-2 md:col-span-2 sm:col-span-4 col-span-2'
                    : '2xl:col-span-2 xl:col-span-2 lg:col-span-2 md:col-span-2 sm:col-span-4 col-span-4'
                }`}>
                <TextFieldControl
                  name={'CardInfo.Exp'}
                  defaultValue=""
                  maxLength={7}
                  fieldHeight={heightField?.height?.[currentDeviceType]}
                  placeholder={paymentPlaceholder?.Exp?.placeholder}
                  header={paymentPlaceholder?.Exp?.headers}
                  placeHolderFontSize={paymentPlaceholder?.Exp?.fontSize}
                  placeHolderweight={paymentPlaceholder?.Exp?.fontWeight}
                  placeHolderLineHeight={paymentPlaceholder?.Exp?.lineHeight}
                  PlaceHolderTextAlign={paymentPlaceholder?.Exp?.textAlign}
                  placeHolderFontFamily={paymentPlaceholder?.Exp?.fontFamily}
                  placeHolderLetterSpace={paymentPlaceholder?.Exp?.letterSpace}
                  radiusBorder={fields?.paymentPlaceholder?.Exp?.border}
                  outline={fields?.paymentPlaceholder?.Exp?.outline}
                  textColor={paymentPlaceholder?.Exp?.textColor}
                  backgroundColor={paymentPlaceholder?.Exp?.textBackgroundColor}
                  placeHolderColor={paymentPlaceholder?.Exp?.color}
                  currentDeviceType={currentDeviceType}
                  onChangeHandler={event => {
                    const e = event as CustomInputEvent;
                    const value = e.target.value
                      .replace(/[^0-9/]/g, '')
                      .replace(/(\/.*?)\/.*/g, '$1');
                    if (
                      value.length === 2 &&
                      e?.nativeEvent?.inputType !== 'deleteContentBackward' &&
                      !value.endsWith('/')
                    ) {
                      return value + '/';
                    }
                    return value;
                  }}
                  rules={{
                    required: 'Expiration date is required',
                    validate: value => {
                      const expDate = value.split('/');
                      if (
                        expDate.length !== 2 ||
                        expDate[0].length !== 2 ||
                        expDate[1].length < 2
                      ) {
                        return 'Expiration date must be in MM/YY format';
                      }
                      const [exMonth, exYear] = expDate;
                      const today = new Date();
                      const someday = new Date();
                      someday.setFullYear(
                        exYear.length === 2
                          ? 2000 + Number(exYear)
                          : Number(exYear),
                        Number(exMonth) - 1,
                        today.getDate(),
                      );
                      if (exMonth > 12 || exMonth < 0 || someday < today) {
                        return 'Expiration date must be a present or future month and year';
                      }
                    },
                  }}
                />
              </div>
              <div
                className={`${
                  useNewExpCvvLayout
                    ? '2xl:col-span-2 xl:col-span-2 lg:col-span-2 md:col-span-2 sm:col-span-4 col-span-2'
                    : '2xl:col-span-2 xl:col-span-2 lg:col-span-2 md:col-span-2 sm:col-span-4 col-span-4'
                }`}>
                <TextFieldControl
                  name={'CardInfo.CVV'}
                  defaultValue=""
                  fieldHeight={heightField?.height?.[currentDeviceType]}
                  radiusBorder={fields?.paymentPlaceholder?.CVV?.border}
                  outline={fields?.paymentPlaceholder?.CVV?.outline}
                  InputIcon={QuestionIcon}
                  tooltipTitle="3-digit security code usually found on the back of your card. American Express cards have a 4-digit code located on the front."
                  placeholder={paymentPlaceholder?.CVV?.placeholder}
                  header={paymentPlaceholder?.CVV?.headers}
                  placeHolderFontSize={paymentPlaceholder?.CVV?.fontSize}
                  placeHolderweight={paymentPlaceholder?.CVV?.fontWeight}
                  textColor={paymentPlaceholder?.CVV?.textColor}
                  backgroundColor={paymentPlaceholder?.CVV?.textBackgroundColor}
                  placeHolderLineHeight={paymentPlaceholder?.CVV?.lineHeight}
                  PlaceHolderTextAlign={paymentPlaceholder?.CVV?.textAlign}
                  placeHolderFontFamily={paymentPlaceholder?.CVV?.fontFamily}
                  placeHolderLetterSpace={paymentPlaceholder?.CVV?.letterSpace}
                  placeHolderColor={paymentPlaceholder?.CVV?.color}
                  currentDeviceType={currentDeviceType}
                  maxLength={4}
                  onChangeHandler={e => {
                    return e.target.value.replace(/[^0-9]/g, '');
                  }}
                  rules={{
                    required: 'CVV is required',
                    validate: value => {
                      if (value.length < 3 || value.length > 4) {
                        return 'CVV must be 3 or 4 digits';
                      }
                    },
                  }}
                />
              </div>
              {isShowNameOnCard && (
                <div className="col-span-4">
                  <TextFieldControl
                    name={'CardInfo.Name'}
                    fieldHeight={heightField?.height?.[currentDeviceType]}
                    radiusBorder={fields?.paymentPlaceholder?.Name?.border}
                    outline={fields?.paymentPlaceholder?.Name?.outline}
                    defaultValue=""
                    maxLength={50}
                    placeholder={paymentPlaceholder?.Name?.placeholder}
                    header={paymentPlaceholder?.Name?.headers}
                    placeHolderFontSize={paymentPlaceholder?.Name?.fontSize}
                    textColor={paymentPlaceholder?.Name?.textColor}
                    backgroundColor={
                      paymentPlaceholder?.Name?.textBackgroundColor
                    }
                    placeHolderweight={paymentPlaceholder?.Name?.fontWeight}
                    placeHolderLineHeight={paymentPlaceholder?.Name?.lineHeight}
                    PlaceHolderTextAlign={paymentPlaceholder?.Name?.textAlign}
                    placeHolderFontFamily={paymentPlaceholder?.Name?.fontFamily}
                    placeHolderLetterSpace={
                      paymentPlaceholder?.Name?.letterSpace
                    }
                    placeHolderColor={paymentPlaceholder?.Name?.color}
                    currentDeviceType={currentDeviceType}
                    onChangeHandler={
                      e => e.target.value.replace(/[^a-zA-Z\s]/g, '') // Only allow letters and spaces
                    }
                    rules={{
                      required: 'Name is required',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {isShowBillingAddress && (
            <section
              aria-labelledby="billing-heading"
              className="col-span-full pt-4">
              <div className="checkbox-control">
                <label className="flex items-center">
                  <Controller
                    name="billing_same_as_shipping"
                    render={({ field: { value, onChange } }) =>
                      fields?.customCheckbox?.enrollmentStyle === 'checkbox' ? (
                        <input
                          type="checkbox"
                          onChange={e => onChange(e.target.checked)}
                          style={{
                            color: fields?.checkboxColor,
                            width: `${fields?.customCheckbox?.checkboxSize}px`,
                            height: `${fields?.customCheckbox?.checkboxSize}px`,
                            borderColor: value
                              ? fields?.customCheckbox?.checkedBorderColor
                              : fields?.customCheckbox?.uncheckedBorderColor,
                          }}
                          checked={value}
                          className={`check-box-billing focus:ring-0 focus:ring-offset-0 h-4 w-4 ${fields?.customCheckbox?.checkboxshape} border-gray-300  focus:outline-none cursor-pointer`}
                        />
                      ) : (
                        <Toggle
                          checked={check}
                          style={{
                            backgroundColor: check
                              ? fields?.customCheckbox?.checkedToggleColor
                              : fields?.customCheckbox?.uncheckedToggleColor,
                            color: fields?.customCheckbox?.checkMarkColor,
                            height: `${fields?.customCheckbox?.toggleSize}px`,
                            width: `${fields?.customCheckbox?.toggleSize}px`,
                          }}
                          circleWidth={`${fields?.customCheckbox?.checkMarkSize}px`}
                          circleHeight={`${fields?.customCheckbox?.checkMarkSize}px`}
                          handleToggle={() => {
                            setCheck(!check);
                            onChange(!check);
                          }}
                        />
                      )
                    }
                  />
                  <div
                    className="ml-2 check-box-same-shipping-address-text cursor-pointer checkbox-content"
                    dangerouslySetInnerHTML={{
                      __html: getQuillEditorText(
                        fields?.customCheckboxText,
                        state,
                      ),
                    }}
                  />
                </label>
              </div>
            </section>
          )}
          {!watch('billing_same_as_shipping') && isShowBillingAddress && (
            <div className="mt-4">
              <h1
                className="font-semibold text-black text-lg mb-4"
                style={{
                  ...getPaddingStyles(
                    billingAddressLabel?.padding,
                    currentDeviceType || 'desktop',
                  ),
                }}
                dangerouslySetInnerHTML={{ __html: billingAddressLabel?.text }}
              />
              <AddressForm
                key="payment-billing-address"
                fieldPrefix="BillingAddress"
                useAppSelector={useAppSelector}
                storeData={storeData}
                cartData={cartData}
                fieldHeight={heightField?.height?.[currentDeviceType]}
                option={option}
                rowGap={heightField?.rowGap?.[currentDeviceType]}
                colGap={heightField?.colGap?.[currentDeviceType]}
                currentDeviceType={currentDeviceType}
              />
            </div>
          )}
          {termsAndSub?.isShowTermsAndSub && (
            <div className="border-2 border-gray-300 rounded-md p-2 mt-5">
              <details className="fk-collapsible-list-details" open={isOpen}>
                <summary
                  onClick={handleToggle}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}>
                  <div className="flex items-center">
                    <span
                      style={{
                        display: 'inline-block',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                        transition: 'transform 0.2s ease-in-out',
                        marginRight: '0.5rem',
                      }}>
                      ▶
                    </span>
                    <input
                      id="terms-of-service"
                      aria-describedby="terms-of-service-description"
                      name="terms_of_service"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 focus:ring-gray-300"
                      onChange={() => {
                        const newIsTermsAccepted = !isTermsAccepted;
                        dispatch(setIsTermsAccepted(newIsTermsAccepted));
                        if (newIsTermsAccepted && cartData && storeData) {
                          publish('AgreeToTerms', {
                            eventId: uuidv4(),
                            shopId: storeData?.ShopID || '',
                            eventName: 'AgreeToTerms',
                            cart: cartData,
                          });
                        }
                      }}
                      checked={isTermsAccepted}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </div>
                  <div className="ml-3">
                    <p
                      className="text-sm"
                      style={{ lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{
                        __html: termsAndSub.terms,
                      }}></p>
                  </div>
                </summary>

                <div
                  className="mt-3"
                  style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                  <p
                    className="text-sm"
                    style={{ lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{
                      __html: termsAndSub?.moreTerms,
                    }}></p>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
